import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Plus, Printer } from 'lucide-react'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { PAPER_SIZES, mmToPx } from '@quebon/shared-learning'
import { aiqApi } from '@/api/aiq'
import { adminDiagnosticsApi } from '@/api/adminDiagnostics'
import type {
  DiagnosticReportResponse,
  DiagnosticReportBasic,
  DiagnosticReportAiq,
  DiagnosticReportLevel,
  DiagnosticReportLevelArea,
  DiagnosticReportLevelItem,
  DiagnosticReportRecommendedCourse,
} from '@/api/adminDiagnostics'
import { useDiagnosticReport } from '@/hooks/admin/useDiagnosticReport'
import { AiqBellCurve } from '@/components/admin/diagnostics/AiqBellCurve'
import { DEFAULT_DIAGNOSTIC_REPORT_VERSION, parseDiagnosticReportVersion } from '@/utils/diagnosticReportVersion'
import type { DiagnosticReportVersion } from '@/utils/diagnosticReportVersion'
import { DiagnosticAiqReportContent } from './DiagnosticAiqReportPages'
import { StudentAiqReportContent } from './StudentAiqReport'

type AssignedCourse = NonNullable<DiagnosticReportRecommendedCourse['course']>

const COURSE_SUMMARY_CONFIG: Record<
  AssignedCourse,
  {
    resultColor: string
    pillColor?: string
    arrowSrc: string
    journeySrc: string
    reasonKey: string
  }
> = {
  A: {
    resultColor: '#87cb34',
    arrowSrc: '/report/diagnostic/icon-arrow-a.svg',
    journeySrc: '/report/diagnostic/level-a.svg',
    reasonKey: 'diagnosticReport.summaryPage.reasonA',
  },
  B: {
    resultColor: '#ffaa00',
    arrowSrc: '/report/diagnostic/icon-arrow-b.svg',
    journeySrc: '/report/diagnostic/level-b.svg',
    reasonKey: 'diagnosticReport.summaryPage.reasonB',
  },
  C: {
    resultColor: '#4599ff',
    arrowSrc: '/report/diagnostic/icon-arrow-c.svg',
    journeySrc: '/report/diagnostic/level-c.svg',
    reasonKey: 'diagnosticReport.summaryPage.reasonC',
  },
  D: {
    resultColor: '#23b8bc',
    arrowSrc: '/report/diagnostic/icon-arrow-d.svg',
    journeySrc: '/report/diagnostic/level-d.svg',
    reasonKey: 'diagnosticReport.summaryPage.reasonD',
  },
  E: {
    resultColor: '#eb6e8e',
    pillColor: '#f17597',
    arrowSrc: '/report/diagnostic/icon-arrow-e.svg',
    journeySrc: '/report/diagnostic/level-e.svg',
    reasonKey: 'diagnosticReport.summaryPage.reasonE',
  },
}

const BAND_COLORS: Record<DiagnosticReportLevel['band'], string> = {
  HIGH: '#4599ff',
  MID: '#ffaa00',
  LOW: '#ff675e',
}

// 첫 쪽에 들어가는 틀린 문제 수: 영역별 결과 카드(낮음)일 때 5, 영역 없는 세트의 정오답표(높음)일 때 3.
const FIRST_WRONG_PAGE_SIZE_WITH_AREAS = 5
const FIRST_WRONG_PAGE_SIZE = 3
const CONTINUED_WRONG_PAGE_SIZE = 12

/** 영역 카드·칩 색. 여기에 없는 영역은 중립 색으로 그린다. */
const LEVEL_AREA_COLORS: Record<string, { solid: string; card: string; chip: string; chipText: string }> = {
  '수와 직관': { solid: '#4599ff', card: 'rgba(69,153,255,0.1)', chip: 'rgba(69,153,255,0.2)', chipText: '#2f7fe0' },
  '연산과 관계': { solid: '#23b8bc', card: 'rgba(35,184,188,0.1)', chip: 'rgba(35,184,188,0.2)', chipText: '#25aaae' },
  '변화와 규칙': { solid: '#6765e0', card: 'rgba(103,101,224,0.1)', chip: 'rgba(103,101,224,0.2)', chipText: '#6765e0' },
  '도형과 측정': { solid: '#ffaa00', card: 'rgba(255,170,0,0.1)', chip: 'rgba(255,170,0,0.2)', chipText: '#ff8c00' },
}
const LEVEL_AREA_FALLBACK_COLOR = { solid: '#888888', card: 'rgba(136,136,136,0.1)', chip: 'rgba(136,136,136,0.2)', chipText: '#666666' }

export const DIAGNOSTIC_A4_PAGE = {
  widthMm: PAPER_SIZES.A4.width,
  heightMm: PAPER_SIZES.A4.height,
  widthPx: mmToPx(PAPER_SIZES.A4.width),
  heightPx: mmToPx(PAPER_SIZES.A4.height),
} as const

const DIAGNOSTIC_PAGE_STYLE = {
  width: DIAGNOSTIC_A4_PAGE.widthPx,
  height: DIAGNOSTIC_A4_PAGE.heightPx,
} as const

export const DIAGNOSTIC_PRINT_PAGE_STYLE = `
  @page { size: ${DIAGNOSTIC_A4_PAGE.widthMm}mm ${DIAGNOSTIC_A4_PAGE.heightMm}mm; margin: 0; }
  @media print {
    .print-action-bar { display: none !important; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
    }
    #diagnostic-report-print {
      width: ${DIAGNOSTIC_A4_PAGE.widthPx}px !important;
    }
    .diagnostic-report-page {
      width: ${DIAGNOSTIC_A4_PAGE.widthPx}px !important;
      height: ${DIAGNOSTIC_A4_PAGE.heightPx}px !important;
      min-height: ${DIAGNOSTIC_A4_PAGE.heightPx}px !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      page-break-after: always !important;
      break-after: page !important;
    }
    html, body, #diagnostic-report-print, #diagnostic-report-print * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

function sanitizeFileNameSegment(value: string, fallback: string): string {
  const sanitized = value
    .trim()
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '')
  return sanitized || fallback
}

function normalizeGradeLabel(value: string): string {
  const gradeLabel = sanitizeFileNameSegment(value, 'grade-unknown')
  return /^(?:초|중|고)\d+$/.test(gradeLabel) ? `${gradeLabel}학년` : gradeLabel
}

interface DiagnosticGradeLabels {
  short: string
  full: string
}

/** Short/long grade labels; the cumulative report reuses this so both reports print the same grade wording. */
export function formatDiagnosticGradeLabels(
  basic: Pick<DiagnosticReportBasic, 'gradeLevel' | 'gradeLabel'>,
  t: TFunction,
): DiagnosticGradeLabels {
  const gradeLevel = basic.gradeLevel?.trim() ?? ''
  const elementaryCode = /^elementary([1-6])$/.exec(gradeLevel)
  const middleCode = /^middle([1-3])$/.exec(gradeLevel)
  const fallbackLabel = basic.gradeLabel.trim() || '-'
  const elementaryLabel = /^초(?:등\s*)?([1-6])(?:학년)?$/.exec(fallbackLabel)
  const middleLabel = /^중(?:등\s*)?([1-3])(?:학년)?$/.exec(fallbackLabel)
  const elementaryGrade = elementaryCode?.[1] ?? elementaryLabel?.[1]
  const middleGrade = middleCode?.[1] ?? middleLabel?.[1]

  if (elementaryGrade) {
    return {
      short: t('diagnosticReport.gradeDisplay.elementaryShort', { grade: elementaryGrade }),
      full: t('diagnosticReport.gradeDisplay.elementaryFull', { grade: elementaryGrade }),
    }
  }
  if (middleGrade) {
    return {
      short: t('diagnosticReport.gradeDisplay.middleShort', { grade: middleGrade }),
      full: t('diagnosticReport.gradeDisplay.middleFull', { grade: middleGrade }),
    }
  }
  if (gradeLevel) {
    const translated = t(`dashboard.gradeLevel.${gradeLevel}`, { defaultValue: fallbackLabel })
    return { short: translated, full: translated }
  }
  return { short: fallbackLabel, full: fallbackLabel }
}

function formatDiagnosticFileTimestamp(value: string | null): string {
  if (!value) return 'diagnosis-time-unknown'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'diagnosis-time-unknown'

  const p = (n: number) => String(n).padStart(2, '0')
  return `${String(date.getFullYear()).slice(-2)}${p(date.getMonth() + 1)}${p(
    date.getDate(),
  )}_${p(date.getHours())}_${p(date.getMinutes())}`
}

export function buildDiagnosticReportFileName(basic: DiagnosticReportBasic): string {
  const learnerName = sanitizeFileNameSegment(basic.nickName, 'student')
  const gradeLabel = normalizeGradeLabel(basic.gradeLabel)
  const diagnosedAt = formatDiagnosticFileTimestamp(basic.diagnosedAt)
  return `${learnerName}_${gradeLabel}_${diagnosedAt}_레벨테스트`
}

/**
 * 진단 리포트 A4 인쇄·PDF 페이지 (spec F2, route students/:userId/diagnostic-report).
 * 화면① 종합결과(basic + 레벨/수학머리 요약 + 최종 배정과정 + 배정이유·학습여정 placeholder)
 * 화면② 레벨테스트 결과(template 문구 + 총점/밴드 + 정오답표 + 틀린문제 상세)
 * 화면③ 수학머리(기존 LC AIQ 리포트 본문 재사용).
 * 기능 우선 — 픽셀 perfection 아님. 배정이유/학습여정/template 문구 = 식별자 분기 placeholder(추후 콘텐츠).
 */
export default function StudentDiagnosticReport() {
  const { t } = useTranslation('admin')
  const { userId } = useParams<{ userId: string }>()
  const numericId = userId ? Number(userId) : NaN
  const validId = Number.isInteger(numericId) && numericId > 0
  // `?version=v1|v2`로 버전을 고른다. 값이 없거나 잘못되면 기본 리포트.
  const [searchParams] = useSearchParams()
  const version = parseDiagnosticReportVersion(searchParams.get('version'))

  const { data, isLoading, isError } = useDiagnosticReport(validId ? numericId : NaN)

  const printRef = useRef<HTMLDivElement>(null)
  const [readyAiqAssignment, setReadyAiqAssignment] = useState<string | null>(null)
  const handleAiqReady = useCallback((assignmentId: string, ready: boolean) => {
    setReadyAiqAssignment(ready ? assignmentId : null)
  }, [])
  const canPrint = !!data && !isLoading && !isError && readyAiqAssignment === data.assignmentId
  const { print: reactToPrintFn, downloadPdf, canDownloadPdf, isDownloading, error: pdfError } = useLmsPrint({
    contentRef: printRef,
    canPrint,
    documentTitle: data ? buildDiagnosticReportFileName(data.basic) : 'diagnostic-report',
    pageStyle: DIAGNOSTIC_PRINT_PAGE_STYLE,
  })

  if (!validId) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('diagnosticReport.invalidParams')}
      </div>
    )
  }
  if (isLoading) {
    return (
      <p className="py-16 text-center text-sm text-gray-400" role="status">
        {t('diagnosticReport.loading')}
      </p>
    )
  }
  if (isError || !data) {
    // fail-soft: 미배정/미채점/403 = 안내
    return (
      <div className="p-8 text-center text-sm text-gray-500" role="status">
        {t('diagnosticReport.notAvailable')}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div
        data-testid="diagnostic-report-actions"
        className="print-action-bar sticky top-12 z-40 flex flex-wrap items-start gap-3 border-b border-gray-200 bg-white px-6 py-3"
      >
        <button
          type="button"
          onClick={() => reactToPrintFn()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          <Printer size={16} />
          {t('diagnosticReport.print')}
        </button>
        {canDownloadPdf && (
          <LmsPdfDownloadButton onClick={downloadPdf} disabled={!canPrint} isDownloading={isDownloading} error={pdfError} />
        )}
      </div>

      <div className="mx-auto py-8" style={{ maxWidth: DIAGNOSTIC_A4_PAGE.widthPx }}>
        <div
          id="diagnostic-report-print"
          ref={printRef}
          className="bg-white shadow-lg print:shadow-none"
          data-testid="diagnostic-report-print"
        >
          <DiagnosticReportPages data={data} version={version}>
            <AiqReportScreen
              version={version}
              learnerId={numericId}
              assignmentId={data.assignmentId}
              onReadyChange={handleAiqReady}
              gradeLabel={formatDiagnosticGradeLabels(data.basic, t).short}
              elapsedMin={data.basic.aiqElapsedMin ?? null}
              pageStart={2 + getLevelPageCount(data.level, version)}
            />
          </DiagnosticReportPages>
        </div>
      </div>
    </div>
  )
}

/** Shared body; the server renderer supplies all AIQ data without additional API queries. */
export function DiagnosticReportPages({
  data,
  version = DEFAULT_DIAGNOSTIC_REPORT_VERSION,
  children,
}: {
  data: DiagnosticReportResponse
  version?: DiagnosticReportVersion
  children: ReactNode
}) {
  return <>
    <CoverScreen basic={data.basic} />
    <SummaryScreen data={data} />
    <LevelScreen basic={data.basic} level={data.level} version={version} />
    {children}
  </>
}

function formatCoverDate(value: string | null): string {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10).replace(/-/g, '. ')

  const p = (n: number) => String(n).padStart(2, '0')
  const weekdays = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getFullYear()}. ${p(date.getMonth() + 1)}. ${p(date.getDate())} (${weekdays[date.getDay()]}) ${p(date.getHours())}:${p(date.getMinutes())}`
}

function formatShortDate(value: string | null): string {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10).replace(/-/g, '. ')

  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}. ${p(date.getMonth() + 1)}. ${p(date.getDate())}`
}

// ── 화면⓪ 표지 ────────────────────────────────────────────────────────────────
function CoverScreen({ basic }: { basic: DiagnosticReportBasic }) {
  const { t } = useTranslation('admin')
  const gradeLabels = formatDiagnosticGradeLabels(basic, t)

  return (
    <section
      data-testid="report-cover"
      className="diagnostic-report-page flex flex-col items-center justify-between bg-white px-14 pb-12 pt-[104px] text-center"
      style={DIAGNOSTIC_PAGE_STYLE}
    >
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-center gap-6 text-2xl font-bold leading-tight text-[#888]">
          <span className="h-5 w-px bg-[#d9d9d9]" aria-hidden="true" />
          <span>{t('diagnosticReport.cover.brandLine')}</span>
          <span className="h-5 w-px bg-[#d9d9d9]" aria-hidden="true" />
        </div>

        <div className="mt-10">
          <p className="text-[46px] font-extrabold leading-tight text-[#131313]">
            {t('diagnosticReport.cover.titleLine1')}
          </p>
          <p className="mt-2 text-[56px] font-extrabold leading-tight text-[#4599ff]">
            {t('diagnosticReport.cover.titleLine2Prefix')}
            <span className="text-[#23b8bc]">{t('diagnosticReport.cover.titleLine2Accent')}</span>
          </p>
        </div>

        <div className="mt-9 text-base leading-normal text-[#131313]">
          <p>
            <strong>{t('diagnosticReport.cover.descriptionStrong1')}</strong>
            {t('diagnosticReport.cover.descriptionMiddle')}
            <strong>{t('diagnosticReport.cover.descriptionStrong2')}</strong>
          </p>
          <p>{t('diagnosticReport.cover.descriptionLine2')}</p>
        </div>

        <img
          src="/report/diagnostic/contents.svg"
          alt={t('diagnosticReport.cover.contentsAlt')}
          className="h-auto w-[620px] max-w-full"
        />
      </div>

      <div className="flex h-[113px] w-full max-w-[663px] items-center justify-between rounded-lg bg-[#f5f5f5] px-8 py-5">
        <img
          src="/report/diagnostic/logo_kor.svg"
          alt={t('diagnosticReport.cover.logoAlt')}
          className="h-6 w-[119px]"
        />
        <dl className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-left text-sm leading-tight">
          <dt className="text-[#888]">{t('diagnosticReport.cover.name')}</dt>
          <dd className="text-[#131313]">{basic.nickName}</dd>
          <dt className="text-[#888]">{t('diagnosticReport.cover.grade')}</dt>
          <dd className="whitespace-nowrap text-[#131313]">{gradeLabels.full}</dd>
          <dt className="text-[#888]">{t('diagnosticReport.cover.diagnosedAt')}</dt>
          <dd className="whitespace-nowrap text-[#131313]">{formatCoverDate(basic.diagnosedAt)}</dd>
        </dl>
      </div>
    </section>
  )
}

// ── 화면① 종합결과 ────────────────────────────────────────────────────────────
function SummaryScreen({ data }: { data: DiagnosticReportResponse }) {
  const { t } = useTranslation('admin')
  const { basic, level, aiq, recommendedCourse } = data
  const course = recommendedCourse?.course ?? null
  const result = getSummaryResult(recommendedCourse, t)
  const levelTimeLimitMin = getLevelTimeLimitMin(basic, level)
  const gradeLabels = formatDiagnosticGradeLabels(basic, t)

  return (
    <section
      data-testid="report-summary"
      className="diagnostic-report-page relative flex break-before-page flex-col gap-8 bg-white px-12 pb-12 pt-10"
      style={DIAGNOSTIC_PAGE_STYLE}
    >
      <header className="flex items-center justify-between border-b border-[#d0d0d0] pb-8">
        <div className="flex items-center gap-3 px-2">
          <img src="/report/diagnostic/logo_kor.svg" alt="" aria-hidden="true" className="h-6 w-[119px]" />
          <span className="h-3.5 w-px bg-[#d0d0d0]" aria-hidden="true" />
          <h1 className="text-base font-bold text-[#131313]">{t('diagnosticReport.summaryPage.title')}</h1>
        </div>
        <div className="flex items-center gap-4 px-2 text-sm text-[#333]">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
              <img
                data-testid="summary-header-user-icon"
                src="/report/diagnostic/icon-user.svg"
                alt=""
                className="size-3.5"
                aria-hidden="true"
              />
            </span>
            <span className="font-medium">{basic.nickName}</span>
            <span className="text-xs text-[#4599ff]">({gradeLabels.short})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
              <img
                data-testid="summary-header-calendar-icon"
                src="/report/diagnostic/icon-calendar.svg"
                alt=""
                className="size-3.5"
                aria-hidden="true"
              />
            </span>
            <span className="font-medium">{formatShortDate(basic.diagnosedAt)}</span>
          </div>
        </div>
      </header>

      <section className="flex w-full flex-col gap-4">
        <SectionTitle>{t('diagnosticReport.summaryPage.basicTitle')}</SectionTitle>
        <div className="grid w-full grid-cols-3 gap-x-2 gap-y-1.5 rounded-lg bg-[#f5f5f5] p-5 text-xs leading-tight">
          <InfoStack
            rows={[
              [t('diagnosticReport.cover.name'), basic.nickName],
              [t('diagnosticReport.cover.grade'), gradeLabels.full],
            ]}
          />
          <InfoStack
            rows={[
              [t('diagnosticReport.summaryPage.levelTimeLabel'), t('diagnosticReport.summaryPage.levelLimit', { value: levelTimeLimitMin })],
              [t('diagnosticReport.summaryPage.aiqTimeLabel'), t('diagnosticReport.summaryPage.aiqElapsed', { value: basic.aiqElapsedMin ?? '—' })],
            ]}
            labelWidth="w-20"
          />
          <InfoStack rows={[[t('diagnosticReport.cover.diagnosedAt'), formatCoverDate(basic.diagnosedAt)]]} />
        </div>
      </section>

      <section className="flex w-full flex-col gap-4">
        <SectionTitle>{t('diagnosticReport.summaryPage.totalTitle')}</SectionTitle>
        <div className="flex flex-col items-center gap-2">
          <div
            data-testid="summary-score-cards"
            className="relative flex h-[310px] w-full items-center justify-between"
          >
            <ScoreCard kind="level" level={level} />
            <ScoreCard kind="aiq" aiq={aiq} />
            <div
              data-testid="summary-score-connector"
              className="absolute left-1/2 top-1/2 z-10 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[6px] border-white bg-[#4599ff]"
            >
              <Plus size={16} strokeWidth={3} className="text-white" aria-hidden="true" />
            </div>
          </div>
          <img src={result.arrowSrc} alt={result.arrowAlt} className="h-[30px] w-[38px]" />
          <div
            data-testid="summary-result-box"
            className={`flex min-h-[95px] w-full items-center justify-center gap-4 rounded-2xl px-5 py-5 ${result.course ? 'text-white' : 'text-[#131313]'}`}
            style={{ backgroundColor: result.resultColor }}
          >
            <img
              data-testid="summary-result-deco-left"
              src="/report/diagnostic/img-deco.svg"
              alt=""
              className="size-6 shrink-0"
              aria-hidden="true"
            />
            {result.course ? (
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-lg font-semibold">{t('diagnosticReport.summaryPage.resultStudent', { name: basic.nickName })}</p>
                <p className="flex items-center gap-1 text-xl font-semibold">
                  <span
                    data-testid="summary-final-course"
                    className="rounded-full bg-white px-2.5 py-0.5 font-bold"
                    style={{ color: result.pillColor }}
                  >
                    {t('diagnosticReport.summaryPage.courseName', { course })}
                  </span>
                  {t('diagnosticReport.summaryPage.courseResultSuffix')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-lg font-semibold">{t('diagnosticReport.summaryPage.resultStudent', { name: basic.nickName })}</p>
                <p data-testid="summary-final-course-empty" className="text-xl font-semibold">
                  {result.emptyMessage}
                </p>
              </div>
            )}
            <img
              data-testid="summary-result-deco-right"
              src="/report/diagnostic/img-deco.svg"
              alt=""
              className="size-6 shrink-0 rotate-180"
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      <section className="flex w-full flex-col gap-4">
        <SectionTitle>{t('diagnosticReport.summaryPage.reasonTitle')}</SectionTitle>
        <div className="rounded-lg bg-[#f5f5f5] p-5 text-xs leading-normal tracking-[-0.6px] text-[#131313]">
          {result.reason}
        </div>
      </section>

      {result.course && (
        <section className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-2 px-2">
            <SectionTitle>{t('diagnosticReport.summaryPage.journeyTitle', { course })}</SectionTitle>
            <p className="text-xs leading-normal tracking-[-0.6px] text-[#131313]">
              {t('diagnosticReport.summaryPage.journeyDesc')}
            </p>
          </div>
          <img
            src={result.journeySrc}
            alt={t('diagnosticReport.summaryPage.journeyAlt', { course })}
            className="h-10 w-full"
          />
        </section>
      )}

      <div className="absolute bottom-4 left-12 flex w-[698px] justify-center px-2 text-sm font-medium text-[#333]">
        1
      </div>
    </section>
  )
}

function getSummaryResult(
  recommendedCourse: DiagnosticReportRecommendedCourse | null,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const course = recommendedCourse?.course ?? null
  if (course) {
    const config = COURSE_SUMMARY_CONFIG[course]
    return {
      course,
      resultColor: config.resultColor,
      pillColor: config.pillColor ?? config.resultColor,
      arrowSrc: config.arrowSrc,
      arrowAlt: t('diagnosticReport.summaryPage.arrowCourseAlt', { course }),
      journeySrc: config.journeySrc,
      reason: recommendedCourse?.reasonKey
        ? t(`diagnosticReport.summaryPage.assignmentReason.${recommendedCourse.reasonKey}`)
        : t(config.reasonKey),
    }
  }

  if (recommendedCourse?.needsAiq) {
    return {
      course: null,
      resultColor: '#f5f5f5',
      pillColor: '#131313',
      arrowSrc: '/report/diagnostic/icon-arrow-none.svg',
      arrowAlt: t('diagnosticReport.summaryPage.arrowNoneAlt'),
      reason: t('diagnosticReport.summary.needsAiq'),
      emptyMessage: t('diagnosticReport.summary.needsAiq'),
    }
  }

  return {
    course: null,
    resultColor: '#f5f5f5',
    pillColor: '#131313',
    arrowSrc: '/report/diagnostic/icon-arrow-none.svg',
    arrowAlt: t('diagnosticReport.summaryPage.arrowNoneAlt'),
    reason: recommendedCourse?.reasonKey
      ? t(`diagnosticReport.summaryPage.assignmentReason.${recommendedCourse.reasonKey}`)
      : t('diagnosticReport.summaryPage.reasonNone'),
    emptyMessage: (
      <>
        <span className="text-[#4599ff]">{t('diagnosticReport.summaryPage.noneResultAccent')}</span>
        {t('diagnosticReport.summaryPage.noneResultSuffix')}
      </>
    ),
  }
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="px-2 text-base font-bold leading-[1.2] tracking-[-0.6px] text-[#131313]">{children}</h2>
}

function InfoStack({ rows, labelWidth = 'w-14' }: { rows: [string, string][]; labelWidth?: string }) {
  return (
    <div className="flex flex-col gap-4 tracking-[-0.6px]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start gap-2">
          <span className={`shrink-0 text-[#888] ${labelWidth}`}>{label}</span>
          <span className="whitespace-nowrap text-[#131313]">{value}</span>
        </div>
      ))}
    </div>
  )
}

function ScoreCard({
  kind,
  level,
  aiq,
}: {
  kind: 'level' | 'aiq'
  level?: DiagnosticReportLevel | null
  aiq?: DiagnosticReportAiq | null
}) {
  const { t } = useTranslation('admin')
  const isLevel = kind === 'level'
  const score = isLevel ? level?.totalScore ?? 0 : aiq?.aiqScore ?? 0
  const band = isLevel ? level?.band ?? 'LOW' : aiq?.band ?? 'LOW'
  const accent = BAND_COLORS[band]
  const message = isLevel
    ? t(`diagnosticReport.summaryPage.levelMessage.${band}`)
    : t(`diagnosticReport.summaryPage.aiqMessage.${band}`)
  const rankLabel = !isLevel && aiq?.aiqRank != null
    ? t(`diagnosticReport.summaryPage.${band === 'LOW' ? 'aiqLowerRank' : 'aiqUpperRank'}`, { rank: aiq.aiqRank })
    : null

  return (
    <div
      data-testid={`summary-score-card-${kind}`}
      className="flex h-[310px] w-[345px] flex-col items-center gap-2 overflow-hidden rounded-2xl bg-[#f5f5f5] p-6"
    >
      <div className="flex items-center gap-1 text-xs font-semibold leading-normal text-[#888]">
        <img
          data-testid={isLevel ? 'summary-level-icon' : 'summary-aiq-icon'}
          src={isLevel ? '/report/diagnostic/icon-board.svg' : '/report/diagnostic/icon-bulb.svg'}
          alt=""
          className="size-3.5 shrink-0"
          aria-hidden="true"
        />
        {isLevel ? t('diagnosticReport.summaryPage.levelResultTitle') : t('diagnosticReport.summaryPage.aiqResultTitle')}
      </div>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex items-center gap-1 leading-tight">
          <span
            data-testid={isLevel ? 'summary-level-score' : 'summary-aiq-score'}
            className="text-[32px] font-bold text-[#131313]"
          >
            {score}
          </span>
          <span className="text-lg font-medium text-[#888]">
            {isLevel ? t('diagnosticReport.summaryPage.outOf100') : rankLabel}
          </span>
        </div>
        {isLevel ? (
          <div
            className="grid size-24 place-items-center rounded-full"
            style={{ background: `conic-gradient(${accent} ${Math.min(Math.max(score, 0), 100)}%, #e7e7ee 0)` }}
          >
            <div className="grid size-[60px] place-items-center rounded-full bg-[#f5f5f5]">
              <span className="text-xl font-bold" style={{ color: accent }}>
                {score}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-[112px] w-[242px]">
            <AiqBellCurve score={score} variant="compact" />
          </div>
        )}
        <p className="min-w-full text-center text-xs font-medium leading-normal text-[#333]">{message}</p>
      </div>
      <BandChips kind={kind} active={band} accent={accent} />
    </div>
  )
}

function BandChips({ kind, active, accent }: { kind: 'level' | 'aiq'; active: DiagnosticReportLevel['band']; accent: string }) {
  const { t } = useTranslation('admin')
  const pointerOffset = active === 'HIGH' ? -82.5 : active === 'LOW' ? 82.5 : 0

  return (
    <div className="relative h-11 w-full">
      <span
        className="absolute top-0 size-0 -translate-x-1/2 border-x-[4px] border-t-[6px] border-x-transparent border-t-[#888]"
        style={{ left: `calc(50% + ${pointerOffset}px)` }}
        aria-hidden="true"
      />
      <div className="absolute bottom-0 left-1/2 grid h-8 w-60 -translate-x-1/2 grid-cols-3 gap-2">
        {(['HIGH', 'MID', 'LOW'] as const).map((band) => (
          <div
            key={band}
            data-testid={`${kind}-band-chip-${band}`}
            className="flex items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: active === band ? accent : '#d0d0d0' }}
          >
            {t(`diagnosticReport.band.${band}`)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── 화면② 레벨테스트 결과 ─────────────────────────────────────────────────────
function ReportPageHeader({ basic, title }: { basic: DiagnosticReportBasic; title: string }) {
  const { t } = useTranslation('admin')
  const gradeLabels = formatDiagnosticGradeLabels(basic, t)

  return (
    <header className="flex items-center justify-between border-b border-[#d0d0d0] pb-8">
      <div className="flex items-center gap-3 px-2">
        <img src="/report/diagnostic/logo_kor.svg" alt="" aria-hidden="true" className="h-6 w-[119px]" />
        <span className="h-3.5 w-px bg-[#d0d0d0]" aria-hidden="true" />
        <h1 className="text-base font-bold text-[#131313]">{title}</h1>
      </div>
      <div className="flex items-center gap-4 px-2 text-sm text-[#333]">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
            <img
              data-testid="report-page-header-user-icon"
              src="/report/diagnostic/icon-user.svg"
              alt=""
              className="size-3.5"
              aria-hidden="true"
            />
          </span>
          <span className="font-medium">{basic.nickName}</span>
          <span className="text-xs text-[#4599ff]">({gradeLabels.short})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
            <img
              data-testid="report-page-header-calendar-icon"
              src="/report/diagnostic/icon-calendar.svg"
              alt=""
              className="size-3.5"
              aria-hidden="true"
            />
          </span>
          <span className="font-medium">{formatShortDate(basic.diagnosedAt)}</span>
        </div>
      </div>
    </header>
  )
}

// ── 화면② 레벨테스트 결과 ─────────────────────────────────────────────────────
function LevelScreen({
  basic,
  level,
  version,
}: {
  basic: DiagnosticReportBasic
  level: DiagnosticReportLevel | null
  version: DiagnosticReportVersion
}) {
  const { t } = useTranslation('admin')

  if (!level) {
    return (
      <div data-testid="report-level">
        <section
          data-testid="report-level-page"
          className="diagnostic-report-page relative flex break-before-page flex-col gap-7 bg-white px-12 pb-12 pt-10"
          style={DIAGNOSTIC_PAGE_STYLE}
        >
          <ReportPageHeader basic={basic} title={t('diagnosticReport.level.title')} />
          <p className="px-2 text-xs text-gray-400">{t('diagnosticReport.level.notGraded')}</p>
          <PageNumber value={2} />
        </section>
      </div>
    )
  }

  const legacy = version === 'v1'
  const areas = legacy ? [] : level.areas ?? []
  const [firstPageWrong, ...continuedWrongPages] = paginateLevelWrongAnswers(level, version)

  return (
    <div data-testid="report-level">
      <section
        data-testid="report-level-page"
        className="diagnostic-report-page relative flex break-before-page flex-col gap-7 bg-white px-12 pb-12 pt-10"
        style={DIAGNOSTIC_PAGE_STYLE}
      >
        <ReportPageHeader basic={basic} title={t('diagnosticReport.level.title')} />
        <LevelSummary basic={basic} level={level} />
        {/* 영역 분류가 없는 세트는 기존 정오답표를 그대로 보여준다. */}
        {areas.length > 0 ? <AreaResults areas={areas} /> : <AnswerSheet items={level.items} />}
        {firstPageWrong.length > 0 && <WrongAnswerTable items={firstPageWrong} showTitle legacy={legacy} />}
        <PageNumber value={2} />
      </section>
      {continuedWrongPages.map((items, index) => (
        <section
          key={items[0]?.no ?? index}
          data-testid="report-level-page"
          className="diagnostic-report-page relative flex break-before-page flex-col gap-7 bg-white px-12 pb-12 pt-10"
          style={DIAGNOSTIC_PAGE_STYLE}
        >
          <ReportPageHeader basic={basic} title={t('diagnosticReport.level.title')} />
          <WrongAnswerTable items={items} legacy={legacy} />
          <PageNumber value={3 + index} />
        </section>
      ))}
    </div>
  )
}

// ── 화면③ 수학머리 진단 결과 ─────────────────────────────────────────────────
function AiqReportScreen({
  version,
  learnerId,
  assignmentId,
  gradeLabel,
  elapsedMin,
  pageStart,
  onReadyChange,
}: {
  version: DiagnosticReportVersion
  learnerId: number
  assignmentId: string
  onReadyChange: (assignmentId: string, ready: boolean) => void
  gradeLabel: string
  elapsedMin: number | null
  pageStart: number
}) {
  const { t } = useTranslation('admin')
  const { data: repAssignment, isLoading: isAssignmentLoading } = useQuery({
    queryKey: ['lc', 'admin', 'diagnosticReportAiqAssignment', learnerId, assignmentId],
    queryFn: async () => {
      const rows = (await adminDiagnosticsApi.list()).data.data ?? []
      return rows.find((row) => row.id === assignmentId) ?? null
    },
    retry: false,
  })
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['aiqDiagnosticReport', learnerId, assignmentId],
    enabled: repAssignment !== undefined,
    queryFn: async () => {
      const res = await aiqApi.getDiagnosticReport(learnerId, assignmentId)
      return res.data.data
    },
    retry: false,
  })
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  const isReady = repAssignment !== undefined && !isAssignmentLoading && !isLoading && (!isError || status === 404)
  useEffect(() => {
    onReadyChange(assignmentId, isReady)
  }, [assignmentId, isReady, onReadyChange])

  if (isAssignmentLoading || isLoading) {
    return (
      <section
        data-testid="diagnostic-aiq-report"
        className="relative min-h-[1024px] break-before-page bg-white px-12 pb-12 pt-10"
      >
        <p className="py-16 text-center text-sm text-gray-400" role="status">
          {t('diagnosticReport.loading')}
        </p>
      </section>
    )
  }

  if (isError && status === 404) {
    return (
      <section
        data-testid="diagnostic-aiq-report"
        className="relative min-h-[1024px] break-before-page bg-white px-12 pb-12 pt-10"
      >
        <div className="rounded-xl border border-gray-100 p-16 text-center text-gray-500">
          {t('studentAiqReport.empty')}
        </div>
      </section>
    )
  }

  if (isError) {
    return (
      <section
        data-testid="diagnostic-aiq-report"
        className="relative min-h-[1024px] break-before-page bg-white px-12 pb-12 pt-10"
      >
        <div className="rounded-xl border border-red-100 p-16 text-center text-sm text-red-500">
          {t('diagnosticReport.notAvailable')}
        </div>
      </section>
    )
  }

  if (!data) {
    return (
      <section
        data-testid="diagnostic-aiq-report"
        className="relative min-h-[1024px] break-before-page bg-white px-12 pb-12 pt-10"
      >
        <div className="rounded-xl border border-gray-100 p-16 text-center text-gray-500">
          {t('studentAiqReport.empty')}
        </div>
      </section>
    )
  }

  const AiqContent = version === 'v1' ? StudentAiqReportContent : DiagnosticAiqReportContent
  return (
    <div data-testid="diagnostic-aiq-report">
      <AiqContent
        data={data}
        gradeLabel={gradeLabel}
        showPrintButton={false}
        itemCount={repAssignment?.aiqTotal ?? null}
        elapsedMin={elapsedMin}
        pageStart={pageStart}
        className="bg-white"
      />
    </div>
  )
}

function LevelSummary({ basic, level }: { basic: DiagnosticReportBasic; level: DiagnosticReportLevel }) {
  const { t } = useTranslation('admin')
  const bandColor = BAND_COLORS[level.band]
  const setMeta = getLevelSetMeta(level.itemCount || level.items.length)
  const itemCount = setMeta.itemCount ?? level.itemCount ?? level.items.length
  const timeLimitMin = setMeta.timeLimitMin ?? basic.levelTimeLimitMin ?? '—'
  const templateKey = level.template ?? 'template-6'

  return (
    <section className="flex w-full flex-col gap-6">
      <div className="flex items-center justify-between pb-2 pr-14">
        <div className="flex flex-col gap-4 px-2">
          <div
            data-testid="level-summary-headline"
            className="text-xl font-bold leading-[1.7] tracking-[-0.6px] text-[#131313]"
          >
            <p>{t('diagnosticReport.levelPage.studentLine', { name: basic.nickName })}</p>
            <p>
              {t('diagnosticReport.levelPage.scorePrefix')}
              <span data-testid="level-total-score" style={{ color: bandColor }}>
                {t('diagnosticReport.levelPage.scoreValue', { score: level.totalScore })}
              </span>
              {t('diagnosticReport.levelPage.scoreMiddle')}
              <span style={{ color: bandColor }}>{t(`diagnosticReport.levelPage.bandSentence.${level.band}`)}</span>
              {t('diagnosticReport.levelPage.scoreSuffix')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-[#eee] px-2.5 py-1 text-sm font-medium text-[#333]">
              {t('diagnosticReport.levelPage.itemCount', { count: itemCount })}
            </span>
            <span className="flex items-center gap-2 rounded-full bg-[#eee] px-2.5 py-1">
              <span className="text-xs font-semibold text-[#888]">{t('diagnosticReport.levelPage.timeLimitLabel')}</span>
              <span className="text-sm font-medium text-[#333]">
                {t('diagnosticReport.levelPage.timeLimitValue', { value: timeLimitMin })}
              </span>
            </span>
          </div>
        </div>
        <LevelBandIndicator name={basic.nickName} active={level.band} />
      </div>

      <div className="flex items-center justify-center gap-2 rounded-lg bg-[#f5f5f5] px-6 py-5">
        <div className="flex w-[459px] flex-col gap-2 text-[#131313] tracking-[-0.6px]">
          <p data-testid="level-template-title" className="whitespace-pre-line text-sm font-bold leading-[1.5]">
            {t(`diagnosticReport.levelTemplate.${templateKey}.title`)}
          </p>
          <p data-testid="level-template-desc" className="whitespace-pre-wrap text-xs font-normal leading-[1.5]">
            {t(`diagnosticReport.levelTemplate.${templateKey}.desc`)}
          </p>
        </div>
        <div className="flex w-[183px] flex-col items-center justify-center gap-4 px-10">
          <div className="flex items-center gap-1 leading-[1.2]">
            <span className="text-2xl font-bold text-[#131313]">{level.totalScore}</span>
            <span className="text-base font-medium text-[#888]">{t('diagnosticReport.summaryPage.outOf100')}</span>
          </div>
          <div
            className="grid size-24 place-items-center rounded-full"
            style={{ background: `conic-gradient(${bandColor} ${Math.min(Math.max(level.totalScore, 0), 100)}%, #d0d0d0 0)` }}
          >
            <div className="grid size-[60px] place-items-center rounded-full bg-[#f5f5f5]">
              <span data-testid="level-score-ring-value" className="text-xl font-bold" style={{ color: bandColor }}>
                {level.totalScore}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function getLevelSetMeta(itemCount: number): { itemCount?: number; timeLimitMin?: number } {
  if (itemCount === 10) return { itemCount: 10, timeLimitMin: 40 }
  if (itemCount === 15) return { itemCount: 15, timeLimitMin: 50 }
  if (itemCount === 20) return { itemCount: 20, timeLimitMin: 60 }
  return { itemCount }
}

function getLevelTimeLimitMin(
  basic: DiagnosticReportBasic,
  level: DiagnosticReportLevel | null,
): number | string {
  if (!level) return basic.levelTimeLimitMin ?? '—'
  const itemCount = level.itemCount || level.items.length
  return getLevelSetMeta(itemCount).timeLimitMin ?? basic.levelTimeLimitMin ?? '—'
}

export function getLevelPageCount(level: DiagnosticReportLevel | null, version: DiagnosticReportVersion = DEFAULT_DIAGNOSTIC_REPORT_VERSION): number {
  if (!level) return 1
  return paginateLevelWrongAnswers(level, version).length
}

function paginateLevelWrongAnswers(level: DiagnosticReportLevel, version: DiagnosticReportVersion): DiagnosticReportLevelItem[][] {
  const items = level.items.filter((it) => !it.isCorrect)
  const showsAreas = version !== 'v1' && (level.areas?.length ?? 0) > 0
  const firstPageSize = showsAreas ? FIRST_WRONG_PAGE_SIZE_WITH_AREAS : FIRST_WRONG_PAGE_SIZE
  return [
    items.slice(0, firstPageSize),
    ...chunk(items.slice(firstPageSize), CONTINUED_WRONG_PAGE_SIZE),
  ]
}

function AreaResults({ areas }: { areas: DiagnosticReportLevelArea[] }) {
  const { t } = useTranslation('admin')
  return (
    <section className="flex w-full flex-col gap-4" data-testid="level-area-results">
      <SectionTitle>{t('diagnosticReport.levelPage.areaTitle')}</SectionTitle>
      <div className="flex w-full items-stretch gap-2">
        {areas.map((area) => {
          const color = LEVEL_AREA_COLORS[area.name] ?? LEVEL_AREA_FALLBACK_COLOR
          return (
            <div
              key={area.name}
              data-testid="level-area-card"
              className="flex min-w-0 flex-1 flex-col items-center gap-2.5 rounded-[10px] px-4 pb-4 pt-3"
              style={{ backgroundColor: color.card }}
            >
              <span
                className="rounded-full px-2.5 py-1 text-xs font-semibold leading-normal text-white"
                style={{ backgroundColor: color.solid }}
              >
                {area.name}
              </span>
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <span data-testid="level-area-correct" className="text-2xl font-bold leading-none text-[#131313]">
                    {area.correctCount}
                  </span>
                  <span className="text-xs font-medium leading-[1.2] text-[#888]">/</span>
                  <span data-testid="level-area-total" className="text-base font-medium leading-[1.2] text-[#888]">
                    {area.itemCount}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-[11px] leading-normal">
                  <span className="text-[#131313]">{t('diagnosticReport.levelPage.areaCorrect')}</span>
                  <span className="text-[8px] font-medium text-[#888]">/</span>
                  <span className="text-[#888]">{t('diagnosticReport.levelPage.areaTotal')}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function LevelBandIndicator({ name, active }: { name: string; active: DiagnosticReportLevel['band'] }) {
  const { t } = useTranslation('admin')
  return (
    <div className="relative h-[106px] w-[174px] shrink-0">
      <div className="absolute left-[50px] top-0 flex w-[124px] flex-col gap-1">
        {(['HIGH', 'MID', 'LOW'] as const).map((band) => (
          <div
            key={band}
            className="flex h-8 items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: active === band ? BAND_COLORS[band] : '#d0d0d0' }}
          >
            {t(`diagnosticReport.band.${band}`)}
          </div>
        ))}
      </div>
      <div
        className="absolute right-[128px] flex h-[18px] items-center gap-1 whitespace-nowrap text-xs font-medium tracking-[-0.6px] text-[#888]"
        style={{ top: active === 'HIGH' ? 7 : active === 'MID' ? 43 : 79 }}
      >
        <span data-testid="level-band-student-name">{name}</span>
        <span className="text-[#888]">▶</span>
      </div>
    </div>
  )
}

function AnswerSheet({ items }: { items: DiagnosticReportLevelItem[] }) {
  const { t } = useTranslation('admin')
  const chunks = chunk(items, 5)

  return (
    <section className="flex w-full flex-col gap-4">
      <SectionTitle>{t('diagnosticReport.levelPage.answerSheetTitle')}</SectionTitle>
      <div className="flex w-full gap-1">
        {chunks.map((itemsChunk) => (
          <table
            key={itemsChunk[0]?.no}
            data-testid="level-answer-table"
            className="w-0 min-w-0 flex-1 table-fixed overflow-hidden rounded-lg border border-[#d0d0d0] border-separate border-spacing-0 text-center"
          >
            <thead>
              <tr className="h-10 bg-[#eee] text-[11px] font-normal leading-normal text-[#131313]">
                <th className="w-1/3 border-b border-r border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.level.col.no')}</th>
                <th className="w-1/3 border-b border-r border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.level.col.difficulty')}</th>
                <th className="w-1/3 border-b border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.level.col.result')}</th>
              </tr>
            </thead>
            <tbody>
              {itemsChunk.map((item, index) => {
                const rowBorder = index < itemsChunk.length - 1 ? 'border-b border-[#d0d0d0]' : ''
                return (
                  <tr key={item.no} className={`h-10 ${index % 2 === 1 ? 'bg-[#f5f5f5]' : 'bg-white'}`}>
                    <td className={`${rowBorder} border-r border-[#d0d0d0] px-2 text-xs font-semibold leading-normal text-[#888]`}>
                      {item.no}
                    </td>
                    <td className={`${rowBorder} border-r border-[#d0d0d0] px-2 text-xs font-semibold leading-normal text-[#131313]`}>
                      {item.difficulty}
                    </td>
                    <td className={`${rowBorder} px-2`}>
                      <AnswerMark correct={item.isCorrect} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ))}
      </div>
    </section>
  )
}

function AnswerMark({ correct }: { correct: boolean }) {
  const label = correct ? 'O' : 'X'

  return (
    <span className="mx-auto block size-5">
      <img
        data-testid={correct ? 'level-answer-mark-correct' : 'level-answer-mark-incorrect'}
        src={correct ? '/report/diagnostic/icon-O.svg' : '/report/diagnostic/icon-X.svg'}
        alt=""
        className="size-5"
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </span>
  )
}

function WrongAnswerTable({
  items,
  showTitle = false,
  legacy = false,
}: {
  items: DiagnosticReportLevelItem[]
  showTitle?: boolean
  /** 개편 전 리포트: 영역 칩과 난이도 열이 없다. */
  legacy?: boolean
}) {
  const { t } = useTranslation('admin')
  const noWidth = legacy ? 'w-20' : 'w-14'
  return (
    <section className="flex w-full flex-col gap-4" data-testid="level-wrong-detail">
      {showTitle && <SectionTitle>{t('diagnosticReport.levelPage.wrongDetailTitle')}</SectionTitle>}
      <table className="w-full overflow-hidden rounded-lg border border-[#d0d0d0] border-separate border-spacing-0 text-xs">
        {showTitle && (
          <thead>
            <tr className="h-10 bg-[#eee] text-[11px] font-normal text-[#131313]">
              <th className={`${noWidth} border-b border-r border-[#d0d0d0] px-2 font-normal`}>{t('diagnosticReport.level.col.no')}</th>
              <th className="w-[300px] border-b border-r border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.levelPage.relatedUnit')}</th>
              <th className="border-b border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.levelPage.checkPoint')}</th>
              {!legacy && (
                <th className="w-16 border-b border-l border-[#d0d0d0] px-2 font-normal">{t('diagnosticReport.levelPage.difficulty')}</th>
              )}
            </tr>
          </thead>
        )}
        <tbody>
          {items.map((item, index) => {
            const relatedUnit = parseLevelHint(item.hint)
            const rowBorder = index < items.length - 1 ? 'border-b border-[#d0d0d0]' : ''
            return (
              <tr key={item.no} className={`${index % 2 === 1 ? 'bg-[#f5f5f5]' : 'bg-white'}`}>
                <td className={`${noWidth} ${rowBorder} border-r border-[#d0d0d0] px-2 py-3 text-center font-semibold leading-normal text-[#888]`}>
                  {item.no}
                </td>
                <td className={`w-[300px] ${rowBorder} border-r border-[#d0d0d0] p-2 text-[#131313]`}>
                  <div className="flex items-center gap-2">
                    {!legacy && item.area && <LevelAreaChip name={item.area} />}
                    <p className="text-[11px] font-normal leading-normal">{relatedUnit.unit}</p>
                  </div>
                  {relatedUnit.detail && (
                    <p className="mt-1 text-xs font-semibold leading-[1.5]">{relatedUnit.detail}</p>
                  )}
                </td>
                <td className={`${rowBorder} break-keep px-2 py-3 text-center text-xs font-semibold leading-[1.5] text-[#131313]`}>
                  {item.checkPoint ?? '-'}
                </td>
                {!legacy && (
                  <td
                    data-testid="level-wrong-difficulty"
                    className={`w-16 ${rowBorder} border-l border-[#d0d0d0] px-2 py-3 text-center text-xs font-semibold leading-normal text-[#131313]`}
                  >
                    {item.difficulty}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}

function LevelAreaChip({ name }: { name: string }) {
  const color = LEVEL_AREA_COLORS[name] ?? LEVEL_AREA_FALLBACK_COLOR
  return (
    <span
      data-testid="level-area-chip"
      className="shrink-0 rounded-full px-1.5 py-[3px] text-[10px] font-semibold leading-normal"
      style={{ backgroundColor: color.chip, color: color.chipText }}
    >
      {name}
    </span>
  )
}

const LEVEL_HINT_CATEGORY_PREFIX = /^\s*\([^()]+\)\s*(?=\[)/

function parseLevelHint(value: string | null): { unit: string; detail: string | null } {
  if (!value) return { unit: '-', detail: null }

  // 새 hint의 선행 분류만 숨기고 단원명/세부 내용의 괄호는 보존한다.
  const displayHint = value.replace(LEVEL_HINT_CATEGORY_PREFIX, '')
  const [unit, ...detailParts] = displayHint.split(' - ')
  const detail = detailParts.join(' - ').trim()
  return {
    unit: unit.trim() || displayHint,
    detail: detail || null,
  }
}

function PageNumber({ value }: { value: number }) {
  return (
    <div
      data-testid="report-page-number"
      className="absolute bottom-4 left-12 flex w-[698px] justify-center px-2 text-sm font-medium text-[#333]"
    >
      {value}
    </div>
  )
}

function chunk<T>(items: T[], size: number): T[][] {
  const result: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size))
  }
  return result
}
